import { useState } from 'react';
import { formatVND, formatDate } from '../../../../utils/format';
import { StatusBadge } from '../../../../components/base/StatusBadge';
import { useModalBackHandler } from '../../../../hooks/useModalBackHandler';
import { getDebtReminderTimeline } from '../../../../utils/debtReminderScheduler';
import { confirmModal } from '../../../../components/base/ConfirmModal';
import { showToast } from '../../../../components/base/Toast';
import { cancelLoan, getFarmerInterestRateOverride } from '../../../../store';
import { estimateAmountOwed, resolveEffectiveRatePercent } from '../../../../utils/interest';
import type { AdvanceRequest, Debt, Season } from '../../../../types';

const CANCELLABLE_STATUSES: AdvanceRequest['status'][] = ['Chờ kiểm tra', 'Cần bổ sung'];

interface LoanDetailModalProps {
  loan: AdvanceRequest;
  season?: Season;
  debt?: Debt;
  hasPendingPayment?: boolean;
  onPayDebt?: (debt: Debt) => void;
  onClose: () => void;
}

const REMINDER_COLORS: Record<string, string> = {
  'T-7 ngày': 'bg-sky-100 text-sky-700',
  'T-3 ngày': 'bg-amber-100 text-amber-700',
  'T-1 ngày': 'bg-orange-100 text-orange-700',
  'T-12 giờ': 'bg-red-100 text-red-700',
  'T-6 giờ': 'bg-red-100 text-red-700',
  'T-1 giờ': 'bg-red-200 text-red-800',
  'Đến hạn': 'bg-red-100 text-red-700',
  'Quá hạn': 'bg-red-200 text-red-800',
};

function formatDateTimeBrief(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export function LoanDetailModal({ loan, season, debt, hasPendingPayment, onPayDebt, onClose }: LoanDetailModalProps) {
  useModalBackHandler(true, onClose);
  const [cancelling, setCancelling] = useState(false);

  const canCancel = CANCELLABLE_STATUSES.includes(loan.status);

  const handleCancel = async () => {
    const ok = await confirmModal({
      title: 'Hủy yêu cầu này?',
      message: 'Yêu cầu vay sẽ bị hủy và không thể khôi phục. Bạn có thể gửi yêu cầu mới sau khi hủy.',
      confirmText: 'Hủy yêu cầu',
      cancelText: 'Đóng',
      variant: 'danger',
    });
    if (!ok) return;
    setCancelling(true);
    try {
      await cancelLoan(loan.id, loan.farmerId);
      showToast('Đã hủy yêu cầu vay', 'success');
      onClose();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Hủy yêu cầu thất bại', 'error');
    } finally {
      setCancelling(false);
    }
  };

  const timeline = (loan.loai === 'Tiền') ? getDebtReminderTimeline(loan.id) : null;
  const hasReminders = timeline ? timeline.some((r) => r.sent) : false;
  const dueDate = debt?.ngayDenHan ? new Date(debt.ngayDenHan) : null;
  const daysLeft = dueDate ? Math.ceil((dueDate.getTime() - Date.now()) / (24 * 60 * 60 * 1000)) : null;

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40 animate-modal-backdrop" onClick={onClose} />
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto animate-modal-slide-up">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-4 py-3 flex items-center justify-between z-10">
          <h3 className="text-base font-semibold text-slate-900">Chi tiết yêu cầu</h3>
          <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="flex flex-wrap gap-2">
            <span className="text-xs font-semibold text-slate-500 bg-slate-100 rounded px-2 py-0.5">{loan.maKhoanVay || `#${loan.id.slice(-6).toUpperCase()}`}</span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
              loan.loai === 'Tiền' ? 'bg-agrida-50 text-agrida-700' : 'bg-sky-50 text-sky-700'
            }`}>
              {loan.loai === 'Tiền' ? 'Tạm ứng tiền' : 'Tạm ứng vật tư'}
            </span>
            <StatusBadge status={loan.status} />
            {loan.isOverLimit && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700">
                Vượt hạn mức
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-slate-400 mb-0.5">Ngày gửi</p>
              <p className="font-medium text-slate-900">{formatDate(loan.ngayYeuCau)}</p>
            </div>
            {loan.ngayDuyet && (
              <div>
                <p className="text-xs text-slate-400 mb-0.5">Ngày duyệt</p>
                <p className="font-medium text-slate-900">{formatDate(loan.ngayDuyet)}</p>
              </div>
            )}
            {season && (
              <div>
                <p className="text-xs text-slate-400 mb-0.5">Mùa vụ</p>
                <p className="font-medium text-slate-900">{season.name}</p>
              </div>
            )}
            {loan.thoiGianMongMuon && (
              <div>
                <p className="text-xs text-slate-400 mb-0.5">Thời gian mong muốn nhận</p>
                <p className="font-medium text-slate-900">{formatDate(loan.thoiGianMongMuon)}</p>
              </div>
            )}
          </div>

          <div>
            <p className="text-xs text-slate-400 mb-0.5">Nội dung đề nghị</p>
            <p className="text-sm text-slate-900 font-medium">{loan.mucDich}</p>
          </div>

          <div className="bg-slate-50 rounded-xl p-3">
            <p className="text-xs text-slate-400 mb-0.5">Số tiền / giá trị đề nghị</p>
            <p className="text-lg font-bold text-agrida-700">{formatVND(loan.soTien)}</p>
          </div>

          {debt && (
            <div className="rounded-xl border border-agrida-100 bg-agrida-50/40 p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div>
                  <p className="text-xs font-semibold uppercase text-agrida-700">Chi tiết khoản vay</p>
                  <p className="text-[10px] text-slate-500">{debt.maKhoanVay || debt.id} · {debt.status}</p>
                </div>
                {daysLeft !== null && (
                  <span className={`rounded-full px-2 py-1 text-[10px] font-semibold ${daysLeft < 0 ? 'bg-rose-100 text-rose-700' : daysLeft <= 7 ? 'bg-amber-100 text-amber-700' : 'bg-white text-agrida-700'}`}>
                    {daysLeft < 0 ? `Quá hạn ${Math.abs(daysLeft)} ngày` : `Còn ${daysLeft} ngày`}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <LoanDebtMetric label="Tiền gốc" value={formatVND(debt.soTien)} />
                <LoanDebtMetric label="Kỳ hạn trả" value={debt.kyHan || '3 tháng'} />
                <LoanDebtMetric label="Đến hạn" value={formatDate(debt.ngayDenHan)} tone={daysLeft !== null && daysLeft <= 7 ? 'rose' : 'slate'} />
                {(() => {
                  // A repayment charges gốc + lãi (frozen on the PaymentIntent);
                  // showing gốc alone here told the farmer 50tr and then asked for 51,2tr.
                  if (debt.conNo <= 0 || !debt.ngayVay || loan.laiSuat === undefined) {
                    return <LoanDebtMetric label="Tổng cần trả" value={formatVND(debt.conNo)} tone="rose" />;
                  }
                  const rate = resolveEffectiveRatePercent(loan.laiSuat, getFarmerInterestRateOverride(debt.farmerId), undefined);
                  const { accruedVnd } = estimateAmountOwed(debt.soTien, rate, debt.ngayVay);
                  return (
                    <>
                      <LoanDebtMetric label="Lãi tạm tính (đến nay)" value={formatVND(accruedVnd)} />
                      <LoanDebtMetric label="Tổng cần trả (gốc + lãi)" value={formatVND(debt.conNo + accruedVnd)} tone="rose" />
                    </>
                  );
                })()}
              </div>
              {debt.conNo > 0 && onPayDebt && !hasPendingPayment && (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onPayDebt(debt);
                  }}
                  className="mt-3 flex min-h-11 w-full items-center justify-center gap-1 rounded-xl bg-agrida-600 px-4 text-sm font-bold text-white"
                >
                  <i className="ri-qr-code-line" />
                  Thanh toán khoản vay
                </button>
              )}
            </div>
          )}

          {/* Reminder Timeline Section */}
          {hasReminders && timeline && (
            <div className="border border-amber-200 rounded-xl p-3 bg-amber-50/30">
              <p className="text-xs text-amber-600 font-medium mb-2 flex items-center gap-1">
                <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-notification-3-line" /></span>
                Lịch sử nhắc trả nợ
              </p>
              <div className="space-y-2">
                {timeline.filter((r) => r.sent).map((r) => (
                  <div key={r.threshold} className="flex items-center gap-2">
                    <span className={`shrink-0 text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${REMINDER_COLORS[r.label] || 'bg-slate-100 text-slate-600'}`}>
                      {r.label}
                    </span>
                    <span className="text-[10px] text-slate-400">{formatDateTimeBrief(r.sentAt)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {loan.loai === 'Vật tư' && (
            <div className="space-y-2 text-sm">
              {loan.nhomVatTu && (
                <div className="flex justify-between">
                  <span className="text-xs text-slate-400">Nhóm vật tư</span>
                  <span className="font-medium text-slate-900">{loan.nhomVatTu}</span>
                </div>
              )}
              {loan.tenVatTu && (
                <div className="flex justify-between">
                  <span className="text-xs text-slate-400">Tên vật tư</span>
                  <span className="font-medium text-slate-900">{loan.tenVatTu}</span>
                </div>
              )}
              {loan.soLuongVatTu && (
                <div className="flex justify-between">
                  <span className="text-xs text-slate-400">Số lượng</span>
                  <span className="font-medium text-slate-900">{loan.soLuongVatTu} {loan.donViVatTu}</span>
                </div>
              )}
            </div>
          )}

          {loan.ghiChu && (
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-xs text-slate-400 mb-0.5">Ghi chú</p>
              <p className="text-sm text-slate-700">{loan.ghiChu}</p>
            </div>
          )}

          {loan.lyDo && (
            <div className="bg-red-50 rounded-xl p-3">
              <p className="text-xs text-red-600 mb-0.5">Lý do từ chối / cần bổ sung</p>
              <p className="text-sm text-red-700">{loan.lyDo}</p>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 flex gap-2 bg-white border-t border-slate-100 px-4 py-3">
          {canCancel && (
            <button
              type="button"
              onClick={handleCancel}
              disabled={cancelling}
              className="flex-1 px-4 py-2.5 rounded-md text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 transition-colors whitespace-nowrap min-h-[44px] disabled:opacity-50"
            >
              {cancelling ? 'Đang hủy...' : 'Hủy yêu cầu'}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-md text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors whitespace-nowrap min-h-[44px]"
          >
            Đóng
          </button>
        </div>
      </div>
    </div>
  );
}

function LoanDebtMetric({ label, value, tone = 'slate' }: { label: string; value: string; tone?: 'slate' | 'amber' | 'rose' }) {
  const color = tone === 'amber' ? 'text-amber-700' : tone === 'rose' ? 'text-rose-700' : 'text-slate-900';
  return (
    <div className="rounded-xl bg-white px-3 py-2">
      <p className="text-[10px] text-slate-400">{label}</p>
      <p className={`mt-0.5 truncate text-xs font-bold ${color}`}>{value}</p>
    </div>
  );
}
